/**
 * BudgetHeader Component
 * Summary statistics row with health indicator
 */

import React from 'react';
import { DollarSign, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';
import type { BudgetHealth } from '../../types/budget';
import { ProgressBar } from '../ui';

export interface BudgetHeaderProps {
  revisedBudget: number;
  committed: number;
  actual: number;
  variance: number;
  variancePercent: number;
  health: BudgetHealth;
  isLoading?: boolean;
}

const BudgetHeader: React.FC<BudgetHeaderProps> = ({
  revisedBudget,
  committed,
  actual,
  variance,
  variancePercent,
  health,
  isLoading = false,
}) => {
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getHealthConfig = (healthStatus: BudgetHealth) => {
    switch (healthStatus) {
      case 'healthy':
        return {
          icon: CheckCircle,
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          label: 'Healthy',
        };
      case 'caution':
        return {
          icon: TrendingUp,
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          label: 'Caution',
        };
      case 'at_risk':
        return {
          icon: AlertTriangle,
          color: 'text-orange-600',
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200',
          label: 'At Risk',
        };
      case 'critical':
        return {
          icon: AlertTriangle,
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          label: 'Critical',
        };
      default:
        return {
          icon: CheckCircle,
          color: 'text-gray-600',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          label: 'Unknown',
        };
    }
  };

  const healthConfig = getHealthConfig(health);
  const HealthIcon = healthConfig.icon;

  const percentCommitted = revisedBudget > 0 ? (committed / revisedBudget) * 100 : 0;
  const percentSpent = revisedBudget > 0 ? (actual / revisedBudget) * 100 : 0;

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-white border border-gray-200 rounded-xl p-6 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-24 mb-3"></div>
            <div className="h-8 bg-gray-200 rounded w-32 mb-2"></div>
            <div className="h-2 bg-gray-200 rounded w-full"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="mb-6">
      {/* Health Indicator */}
      <div
        className={`inline-flex items-center space-x-2 px-4 py-2 rounded-lg border ${healthConfig.bgColor} ${healthConfig.borderColor} mb-4`}
      >
        <HealthIcon className={`h-5 w-5 ${healthConfig.color}`} />
        <span className={`font-medium text-sm ${healthConfig.color}`}>
          Budget Health: {healthConfig.label}
        </span>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Revised Budget */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 hover:border-brand-300 transition-colors">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-600">Revised Budget</span>
            <DollarSign className="h-5 w-5 text-brand-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-2">
            {formatCurrency(revisedBudget)}
          </div>
          <div className="text-xs text-gray-500">Total allocated budget</div>
        </div>

        {/* Committed */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 hover:border-brand-300 transition-colors">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-600">Committed</span>
            <DollarSign className="h-5 w-5 text-blue-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-2">{formatCurrency(committed)}</div>
          <ProgressBar
            value={percentCommitted}
            variant="info"
            size="sm"
            showLabel={true}
            className="mb-1"
          />
          <div className="text-xs text-gray-500">{percentCommitted.toFixed(1)}% committed</div>
        </div>

        {/* Actual */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 hover:border-brand-300 transition-colors">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-600">Actual</span>
            <DollarSign className="h-5 w-5 text-green-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-2">{formatCurrency(actual)}</div>
          <ProgressBar
            value={percentSpent}
            variant="success"
            size="sm"
            showLabel={true}
            className="mb-1"
          />
          <div className="text-xs text-gray-500">{percentSpent.toFixed(1)}% spent</div>
        </div>

        {/* Variance */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 hover:border-brand-300 transition-colors">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-600">Variance</span>
            <TrendingUp
              className={`h-5 w-5 ${variance >= 0 ? 'text-green-600' : 'text-red-600'}`}
            />
          </div>
          <div
            className={`text-2xl font-bold mb-2 ${variance >= 0 ? 'text-green-600' : 'text-red-600'}`}
          >
            {variance >= 0 ? '+' : ''}
            {formatCurrency(variance)}
          </div>
          <div className="text-xs text-gray-500">
            {variance >= 0 ? 'Under' : 'Over'} budget by {Math.abs(variancePercent).toFixed(1)}%
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetHeader;
